import moment from "moment";
import React, { useId } from "react";
import { useTranslation } from "react-i18next";

// Mirrors the mobile app's PUSH dashboard ("Your day at a glance"). Colors come from
// msb-mobile's appTheme / colorsHelper so the preview matches what athletes see.
const DEFAULT_PRIMARY_COLOR = "#5BC500";

const THEMES = {
  dark: {
    background: "#000000",
    card: "#1C1C1E",
    border: "#2C2C2E",
    infoSurface: "#2C2C2E",
    track: "#3A3A3C",
    textPrimary: "#FFFFFF",
    textSecondary: "#9898A0",
    tabInactive: "#8E8E93",
    danger: "#FF453A",
  },
  light: {
    background: "#F3F2F8",
    card: "#FFFFFF",
    border: "#E5E5EA",
    infoSurface: "#F3F2F8",
    track: "#D1D1D6",
    textPrimary: "#000000",
    textSecondary: "#8A8A8E",
    tabInactive: "#8E8E93",
    danger: "#FF3B30",
  },
};

// Stroke paths on a 24x24 grid.
const ICONS = {
  bell: "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0",
  chat: "M7.9 20A9 9 0 1 0 4 16.1L2 22Z",
  chevronLeft: "m15 18-6-6 6-6",
  chevronRight: "m9 18 6-6-6-6",
  chevronUp: "m18 15-6-6-6 6",
  percent:
    "M19 5 5 19M6.5 4a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM17.5 15a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z",
  measures:
    "M12 2.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4ZM5 9h14M12 9v6M8.5 22l3.5-7 3.5 7",
  camera:
    "M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3ZM12 10a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z",
  meal: "M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2M7 2v20M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7",
  steps: "M22 12h-4l-3 9L9 3l-3 9H2",
  sleep: "M2 4v16M2 8h18a2 2 0 0 1 2 2v10M2 17h20M6 8v9",
  water:
    "M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7Z",
  plus: "M5 12h14M12 5v14",
  calendar:
    "M8 2v4M16 2v4M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM3 10h18",
  widgets:
    "M4 3h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1ZM14 3h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1ZM4 13h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1ZM14 13h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1Z",
  reports:
    "M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2ZM8 17v-4M12 17V8M16 17v-6",
};

// Filled shapes on a 24x24 grid.
const FILLED_ICONS = {
  document: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z",
  star: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01Z",
  home: "M3 9.5 12 2l9 7.5V20a2 2 0 0 1-2 2h-5v-6h-4v6H5a2 2 0 0 1-2-2Z",
};

// MSB logo on a 48x48 grid (msb-mobile's MSBSVGLogo paths and transforms).
const MSB_LOGO_PATHS = [
  {
    transform: "translate(-120.997 -348.205)",
    d: "M143.654 389.408c-1.449-.683-6.617-4.029-6.622-17.165v-.066c0-13.119 5.173-16.492 6.622-17.176a.079.079 0 00-.038-.153c-8.383 0-10.466 8.571-10.938 14.054h-3.352a1.416 1.416 0 00-1.367.908 7.164 7.164 0 000 4.778 1.433 1.433 0 001.373.919h3.341c.492 5.469 2.576 14.029 10.964 14.054a.079.079 0 00.017-.153z",
  },
  {
    transform: "translate(-140.629 -348.204)",
    d: "M182.02 389.407c-1.449-.683-6.617-4.029-6.628-17.165v-.066c0-13.119 5.178-16.492 6.628-17.176a.08.08 0 00-.044-.153c-10.389.038-11.106 13.2-11.106 17.329v.066c0 4.133.7 17.291 11.106 17.318a.08.08 0 10.044-.153z",
  },
  {
    transform: "translate(-132.756 -345.818)",
    d: "M158.448 369.841v-.06c0-14.874 7.415-19.242 9.023-20.018a.082.082 0 00-.038-.159c-12.375-.662-13.971 16.229-13.933 20.177v.06c-.049 3.919 1.547 20.845 13.944 20.193a.082.082 0 00.038-.159c-1.582-.775-9.034-5.138-9.034-20.034z",
  },
];

const Icon = ({ name, x, y, size = 24, color, strokeWidth = 2 }) => (
  <g transform={`translate(${x} ${y}) scale(${size / 24})`}>
    {FILLED_ICONS[name] ? (
      <path d={FILLED_ICONS[name]} fill={color} />
    ) : (
      <path
        d={ICONS[name]}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </g>
);

const RING_RADIUS = 24;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const DailyDashboardPreview = (props) => {
  const { t } = useTranslation("dashboard");
  const { brandingColor, theme, isDefaultColor, secondaryTextColor } = props;
  // Several previews can be mounted on one page; ids must stay unique.
  const idPrefix = useId().replace(/:/g, "");
  const checkInClipId = `${idPrefix}-check-in-clip`;
  const feedbackGradientId = `${idPrefix}-feedback-gradient`;
  const fabShadowId = `${idPrefix}-fab-shadow`;

  const colors = THEMES[theme === "light" ? "light" : "dark"];
  const primaryColor =
    brandingColor && !isDefaultColor ? brandingColor : DEFAULT_PRIMARY_COLOR;
  // Text/icons drawn on top of the primary color (mobile's primaryBgTextColor).
  const onPrimaryColor = secondaryTextColor || "#FFFFFF";
  const upper = (key) => t(key).toLocaleUpperCase();

  const quickLogItems = [
    { icon: "percent", label: upper("dailyDashboardPreview.bodyFat") },
    { icon: "measures", label: upper("dailyDashboardPreview.measures") },
    { icon: "camera", label: upper("dailyDashboardPreview.photo") },
    { icon: "meal", label: upper("dailyDashboardPreview.mealPhoto") },
  ];

  const goalTiles = [
    {
      icon: "steps",
      label: t("dailyDashboardPreview.steps"),
      value: "6,240",
      goal: "/10,000",
      unit: t("dailyDashboardPreview.steps"),
      progress: 0.62,
    },
    {
      icon: "sleep",
      label: t("dailyDashboardPreview.sleep"),
      value: "7:15",
      goal: "/8:00",
      unit: t("dailyDashboardPreview.hrs"),
      progress: 0.9,
    },
    {
      icon: "water",
      label: t("dailyDashboardPreview.water"),
      value: "2",
      goal: "/3",
      unit: t("dailyDashboardPreview.liters"),
      progress: 0.67,
      hasAddButton: true,
    },
  ];

  // Same palette as mobile's nutrition rings (protein, carbs, fat, fiber).
  const rings = [
    { color: "#E90B34", value: "112", progress: 0.62 },
    { color: "#FB9C01", value: "164", progress: 0.66 },
    { color: "#015EF5", value: "38", progress: 0.54 },
    { color: "#0EBF67", value: "21", progress: 0.7 },
  ];

  const tabs = [
    { icon: "home", label: t("dailyDashboardPreview.dashboard") },
    { icon: "calendar", label: t("dailyDashboardPreview.calendar") },
    { icon: "widgets", label: t("dailyDashboardPreview.widgets") },
    { icon: "reports", label: t("dailyDashboardPreview.reports") },
    { icon: null, label: t("dailyDashboardPreview.profile") },
  ];

  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="360"
      height="779"
      viewBox="0 0 360 779"
      fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif"
    >
      <defs>
        <clipPath id={checkInClipId}>
          <rect x="16" y="200" width="328" height="72" rx="12" />
        </clipPath>
        <linearGradient id={feedbackGradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={primaryColor} stopOpacity="0.22" />
          <stop offset="1" stopColor={primaryColor} stopOpacity="0" />
        </linearGradient>
        <filter id={fabShadowId} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.3" />
        </filter>
      </defs>

      <rect width="360" height="779" fill={colors.background} />

      {/* Status bar */}
      <text
        x="34"
        y="31"
        fontSize="16"
        fontWeight="600"
        fill={colors.textPrimary}
      >
        9:41
      </text>
      {[0, 1, 2, 3].map((bar) => (
        <rect
          key={bar}
          x={280 + bar * 5}
          y={29 - (bar + 1) * 3}
          width="3"
          height={(bar + 1) * 3}
          rx="1"
          fill={colors.textPrimary}
        />
      ))}
      <rect
        x="305"
        y="20"
        width="24"
        height="12"
        rx="3.5"
        fill="none"
        stroke={colors.textPrimary}
        strokeOpacity="0.4"
      />
      <rect
        x="307"
        y="22"
        width="18"
        height="8"
        rx="2"
        fill={colors.textPrimary}
      />

      {/* Notifications and chat */}
      <Icon name="bell" x={268} y={60} color={colors.textPrimary} />
      <Icon name="chat" x={310} y={60} color={colors.textPrimary} />
      <circle cx="334" cy="60" r="8" fill={colors.danger} />
      <text
        x="334"
        y="64"
        fontSize="10"
        fontWeight="700"
        fill="#FFFFFF"
        textAnchor="middle"
      >
        3
      </text>

      {/* Header */}
      <text
        x="16"
        y="125"
        fontSize="26"
        fontWeight="700"
        letterSpacing="-0.5"
        fill={colors.textPrimary}
      >
        {t("dailyDashboardPreview.title")}
      </text>
      <rect
        x="304"
        y="98"
        width="40"
        height="40"
        rx="12"
        fill={colors.card}
        stroke={colors.border}
      />
      <circle
        cx="324"
        cy="118"
        r="8"
        fill="none"
        stroke={colors.textPrimary}
        strokeWidth="3"
        strokeDasharray="3 3.28"
      />
      <circle
        cx="324"
        cy="118"
        r="6"
        fill={colors.card}
        stroke={colors.textPrimary}
        strokeWidth="1.5"
      />
      <circle
        cx="324"
        cy="118"
        r="2.5"
        fill="none"
        stroke={colors.textPrimary}
        strokeWidth="1.5"
      />

      {/* Date navigation */}
      {[16, 304].map((x) => (
        <React.Fragment key={x}>
          <rect
            x={x}
            y="148"
            width="40"
            height="40"
            rx="12"
            fill={colors.card}
            stroke={colors.border}
          />
          <Icon
            name={x === 16 ? "chevronLeft" : "chevronRight"}
            x={x + 10}
            y={158}
            size={20}
            color={colors.textPrimary}
          />
        </React.Fragment>
      ))}
      <text
        x="180"
        y="163"
        fontSize="10"
        fontWeight="700"
        letterSpacing="1"
        fill={colors.textPrimary}
        textAnchor="middle"
      >
        {upper("dailyDashboardPreview.today")}
      </text>
      <text
        x="180"
        y="182"
        fontSize="15"
        fontWeight="700"
        fill={colors.textPrimary}
        textAnchor="middle"
      >
        {moment().format("ll")}
      </text>

      {/* Check-In Day banner */}
      <rect
        x="16"
        y="200"
        width="328"
        height="72"
        rx="12"
        fill={primaryColor}
        stroke={colors.border}
      />
      {/* Dimmed MSB logo watermark, same paths as mobile's MSBSVGLogo */}
      <g clipPath={`url(#${checkInClipId})`}>
        <g
          transform="translate(204 159) scale(3.2)"
          fill={onPrimaryColor}
          fillOpacity="0.1"
        >
          {MSB_LOGO_PATHS.map((path) => (
            <path key={path.d} d={path.d} transform={path.transform} />
          ))}
        </g>
      </g>
      <circle cx="60" cy="236" r="22" fill={onPrimaryColor} fillOpacity="0.1" />
      <Icon name="document" x={49} y={225} size={22} color={onPrimaryColor} />
      <text x="96" y="232" fontSize="17" fontWeight="600" fill={onPrimaryColor}>
        {t("dailyDashboardPreview.checkInDay")}
      </text>
      <text x="96" y="252" fontSize="13" fill={onPrimaryColor}>
        {t("dailyDashboardPreview.dueToday")}
      </text>
      <Icon
        name="chevronRight"
        x={312}
        y={224}
        color={onPrimaryColor}
        strokeWidth={2.5}
      />

      {/* Feedback banner */}
      <rect
        x="16"
        y="282"
        width="328"
        height="72"
        rx="12"
        fill={colors.card}
        stroke={colors.border}
      />
      <rect
        x="16"
        y="282"
        width="328"
        height="72"
        rx="12"
        fill={`url(#${feedbackGradientId})`}
      />
      <circle cx="60" cy="318" r="22" fill={primaryColor} />
      <Icon name="star" x={49} y={307} size={22} color={onPrimaryColor} />
      <text
        x="96"
        y="313"
        fontSize="17"
        fontWeight="600"
        fill={colors.textPrimary}
      >
        {t("dailyDashboardPreview.feedback")}
      </text>
      <text x="96" y="333" fontSize="12" fill={colors.textSecondary}>
        {t("dailyDashboardPreview.feedbackDescription")}
      </text>
      <Icon
        name="chevronRight"
        x={312}
        y={306}
        color={colors.textSecondary}
        strokeWidth={2.5}
      />

      {/* Quick Log */}
      <text
        x="16"
        y="385"
        fontSize="13"
        fontWeight="700"
        letterSpacing="1.2"
        fill={colors.textSecondary}
      >
        {upper("dailyDashboardPreview.quickLog")}
        <tspan dx="8" fontSize="11" fill={colors.textSecondary}>
          0/9
        </tspan>
      </text>
      {quickLogItems.map((item, index) => {
        const x = 16 + index * 98;
        return (
          <g key={item.icon}>
            <rect
              x={x}
              y="398"
              width="88"
              height="84"
              rx="12"
              fill={colors.card}
            />
            <Icon
              name={item.icon}
              x={x + 34}
              y={412}
              size={20}
              color={colors.textSecondary}
            />
            <text
              x={x + 44}
              y="452"
              fontSize="10"
              fontWeight="600"
              fill={colors.textSecondary}
              textAnchor="middle"
            >
              {item.label}
            </text>
            <text
              x={x + 44}
              y="468"
              fontSize="10"
              fontWeight="700"
              fill={primaryColor}
              textAnchor="middle"
            >
              {upper("dailyDashboardPreview.log")}
            </text>
          </g>
        );
      })}

      {/* Daily Goals */}
      <rect
        x="16"
        y="500"
        width="328"
        height="300"
        rx="12"
        fill={colors.card}
        stroke={colors.border}
      />
      <rect
        x="32"
        y="516"
        width="4"
        height="18"
        rx="2"
        fill={colors.textSecondary}
      />
      <text
        x="46"
        y="530"
        fontSize="13"
        fontWeight="700"
        letterSpacing="1.2"
        fill={colors.textSecondary}
      >
        {upper("dailyDashboardPreview.dailyGoals")}
      </text>
      <text
        x="306"
        y="530"
        fontSize="13"
        fill={colors.textSecondary}
        textAnchor="end"
      >
        1/3
      </text>
      <Icon
        name="chevronUp"
        x={312}
        y={516}
        size={18}
        color={colors.textPrimary}
        strokeWidth={2.5}
      />
      {goalTiles.map((tile, index) => {
        const x = 28 + index * 104;
        return (
          <g key={tile.icon}>
            <rect
              x={x}
              y="548"
              width="96"
              height="96"
              rx="10"
              fill={colors.infoSurface}
            />
            <Icon
              name={tile.icon}
              x={x + 8}
              y={557}
              size={15}
              color={colors.textSecondary}
            />
            <text x={x + 28} y="569" fontSize="12" fill={colors.textSecondary}>
              {tile.label}
            </text>
            {tile.hasAddButton ? (
              <>
                <rect
                  x={x + 74}
                  y="556"
                  width="14"
                  height="14"
                  rx="3"
                  fill={primaryColor}
                />
                <Icon
                  name="plus"
                  x={x + 75}
                  y={557}
                  size={12}
                  color={onPrimaryColor}
                  strokeWidth={3}
                />
              </>
            ) : null}
            <text
              x={x + 10}
              y="600"
              fontSize="15"
              fontWeight="700"
              fill={colors.textPrimary}
            >
              {tile.value}
              <tspan fontSize="12" fontWeight="400" fill={colors.textSecondary}>
                {tile.goal}
              </tspan>
            </text>
            <text x={x + 10} y="616" fontSize="11" fill={colors.textSecondary}>
              {tile.unit}
            </text>
            <rect
              x={x + 10}
              y="628"
              width="76"
              height="4"
              rx="2"
              fill={colors.track}
            />
            <rect
              x={x + 10}
              y="628"
              width={76 * tile.progress}
              height="4"
              rx="2"
              fill={primaryColor}
            />
          </g>
        );
      })}
      <rect
        x="28"
        y="654"
        width="304"
        height="110"
        rx="10"
        fill={colors.infoSurface}
      />
      {rings.map((ring, index) => {
        const cx = 66 + index * 76;
        return (
          <g key={ring.color}>
            <circle
              cx={cx}
              cy="694"
              r={RING_RADIUS}
              fill="none"
              stroke={colors.track}
              strokeWidth="6"
            />
            <circle
              cx={cx}
              cy="694"
              r={RING_RADIUS}
              fill="none"
              stroke={ring.color}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${RING_CIRCUMFERENCE * ring.progress} ${RING_CIRCUMFERENCE}`}
              transform={`rotate(-90 ${cx} 694)`}
            />
            <text
              x={cx}
              y="694"
              dominantBaseline="central"
              fontSize="13"
              fontWeight="700"
              fill={colors.textPrimary}
              textAnchor="middle"
            >
              {ring.value}
            </text>
          </g>
        );
      })}

      {/* Quick Log floating button */}
      <circle
        cx="314"
        cy="650"
        r="28"
        fill={primaryColor}
        filter={`url(#${fabShadowId})`}
      />
      <Icon
        name="plus"
        x={302}
        y={638}
        color={onPrimaryColor}
        strokeWidth={2.5}
      />
      <circle
        cx="336"
        cy="628"
        r="10"
        fill={onPrimaryColor}
        stroke={colors.background}
        strokeWidth="2"
      />
      <text
        x="336"
        y="632"
        fontSize="11"
        fontWeight="700"
        fill={primaryColor}
        textAnchor="middle"
      >
        4
      </text>

      {/* Tab bar */}
      <rect x="0" y="703" width="360" height="76" fill={colors.card} />
      <line x1="0" y1="703.5" x2="360" y2="703.5" stroke={colors.border} />
      {tabs.map((tab, index) => {
        // 12px side padding, five equal slots.
        const cx = 46 + index * 67;
        const tabColor = index === 0 ? primaryColor : colors.tabInactive;
        return (
          <g key={tab.label}>
            {tab.icon ? (
              <Icon
                name={tab.icon}
                x={cx - 12}
                y={712}
                color={tabColor}
                strokeWidth={1.8}
              />
            ) : (
              <>
                <circle cx={cx} cy="724" r="12" fill={colors.track} />
                <circle cx={cx} cy="720" r="4.5" fill={colors.tabInactive} />
                <path
                  d={`M${cx - 8} ${732}a8 6.5 0 0 1 16 0`}
                  fill={colors.tabInactive}
                />
              </>
            )}
            <text
              x={cx}
              y="752"
              fontSize="11"
              fontWeight={index === 0 ? "600" : "400"}
              fill={tabColor}
              textAnchor="middle"
            >
              {tab.label}
            </text>
          </g>
        );
      })}
      <rect
        x="120"
        y="764"
        width="120"
        height="5"
        rx="2.5"
        fill={colors.textPrimary}
      />
    </svg>
  );
};

export default DailyDashboardPreview;
